import React from 'react';
import { YStack, XStack, SizableText, H4, Card, ScrollView } from 'tamagui';
import { View } from 'react-native';
import type { UIComponentContract } from '../../types/componentContract';

export const uiContract: UIComponentContract = {
  id: "items.table",
  accepts: {
    capability: "list_items",
    shape: {}, // Accepts any structure (handled in component)
    required: [],
    optional: []
  },
  renders: "table",
  priority: 7,
  surface: "options",
  fallback: true
};

interface ItemsTableProps {
  data?: any[] | { data?: any[] };
}

// Helper function to extract items array (outside component to avoid hook issues)
function extractItems(data: any): any[] {
  if (!data) return [];
  
  if (Array.isArray(data)) {
    return data;
  }
  
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data.data)) {
      return data.data;
    }
    
    // Try to find array property
    try {
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
    } catch (e) {
      // Ignore errors
    }
  }
  
  return [];
}

// Helper function to extract columns from items (outside component)
function extractColumns(items: any[]): string[] {
  if (!items || items.length === 0) return [];
  
  const firstItem = items[0];
  if (typeof firstItem === 'object' && firstItem !== null) {
    try {
      return Object.keys(firstItem).filter(key => {
        const value = firstItem[key];
        // Exclude nested objects and functions
        return value !== null && 
               value !== undefined && 
               typeof value !== 'object' && 
               typeof value !== 'function';
      });
    } catch (e) {
      return [];
    }
  }
  return [];
}

export const ItemsTable: React.FC<ItemsTableProps> = (props) => {
  const { data } = props || {};
  const items = extractItems(data);
  const columns = extractColumns(items);

  if (!items || items.length === 0) {
    return (
      <Card borderRadius={16} padding="$5" elevate backgroundColor="$background">
        <SizableText color="$gray10">No items available</SizableText>
      </Card>
    );
  }

  return (
    <Card
      borderRadius={16}
      padding="$5"
      elevate
      backgroundColor="$background"
    >
      <YStack gap="$3">
        <H4 fontWeight="700">Items ({items.length})</H4>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <ScrollView maxHeight={500}>
            <View style={{ borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              {/* Table Header */}
              <View style={{ 
                flexDirection: 'row', 
                backgroundColor: '#f5f5f5',
                borderBottomWidth: 2,
                borderBottomColor: '#e0e0e0'
              }}>
                {columns.map((col, index) => (
                  <View
                    key={col}
                    style={{
                      padding: 12,
                      minWidth: 120,
                      borderRightWidth: index < columns.length - 1 ? 1 : 0,
                      borderRightColor: '#e0e0e0'
                    }}
                  >
                    <SizableText size="$4" fontWeight="700" color="$gray12">
                      {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SizableText>
                  </View>
                ))}
              </View>

              {/* Table Body */}
              {items.map((item, rowIndex) => (
                <View
                  key={item.id || item._id || item.number || rowIndex}
                  style={{
                    flexDirection: 'row',
                    backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                    borderBottomWidth: rowIndex < items.length - 1 ? 1 : 0,
                    borderBottomColor: '#e0e0e0'
                  }}
                >
                  {columns.map((col, colIndex) => {
                    const value = item[col];
                    let displayValue: string = '';
                    
                    if (value === null || value === undefined) {
                      displayValue = '-';
                    } else if (typeof value === 'boolean') {
                      displayValue = value ? 'Yes' : 'No';
                    } else if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else {
                      displayValue = String(value);
                    }

                    return (
                      <View
                        key={col}
                        style={{
                          padding: 12,
                          minWidth: 120,
                          borderRightWidth: colIndex < columns.length - 1 ? 1 : 0,
                          borderRightColor: '#e0e0e0'
                        }}
                      >
                        <SizableText 
                          size="$3" 
                          color="$gray11"
                          numberOfLines={2}
                        >
                          {displayValue}
                        </SizableText>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          </ScrollView>
        </ScrollView>
      </YStack>
    </Card>
  );
};

// Attach contract to component
(ItemsTable as any).uiContract = uiContract;

export default ItemsTable;

